'use client';

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Slider } from '@/components/ui/slider';
import { addDays, format } from 'date-fns';
import type { FieldError as FormFieldError } from 'react-hook-form';

const clampDays = (value: number, min: number, max: number) =>
  Math.min(Math.max(Math.round(value), min), max);

interface DaysAheadFieldProps {
  id: string;
  label: string;
  description: string;
  value: number | undefined;
  min: number;
  max: number;
  error?: FormFieldError;
  /** Every edit, including a half-typed or empty box. */
  onChange: (value: number | undefined) => void;
  /** A settled value: a slider move, or the box losing focus (clamped). */
  onCommit: (value: number) => void;
  onBlur: () => void;
}

/**
 * One booking-window limit: a slider for quick setting and a number box for
 * an exact figure. Both drive the same form value. The box can be empty or
 * out of range while typing; it clamps to the slider's range when it loses
 * focus, so what is saved is always something the slider can show.
 */
export function DaysAheadField({
  id,
  label,
  description,
  value,
  min,
  max,
  error,
  onChange,
  onCommit,
  onBlur,
}: DaysAheadFieldProps) {
  const sliderValue = clampDays(value ?? min, min, max);
  return (
    <Field data-invalid={!!error}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FieldLabel htmlFor={id} className="font-medium">
          {label}
        </FieldLabel>
        <InputGroup className="w-32">
          <InputGroupInput
            id={id}
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            step={1}
            value={value ?? ''}
            aria-invalid={!!error}
            onChange={(event) =>
              onChange(
                event.target.value === ''
                  ? undefined
                  : event.target.valueAsNumber
              )
            }
            onBlur={() => {
              if (typeof value === 'number' && Number.isFinite(value)) {
                onCommit(clampDays(value, min, max));
              }
              onBlur();
            }}
            className="text-right tabular-nums"
          />
          <InputGroupAddon align="inline-end" className="text-muted-foreground">
            {value === 1 ? 'day' : 'days'}
          </InputGroupAddon>
        </InputGroup>
      </div>
      <FieldDescription>{description}</FieldDescription>
      <Slider
        value={[sliderValue]}
        onValueChange={([days]) => onCommit(days)}
        min={min}
        max={max}
        step={1}
        aria-label={label}
        className="mt-1"
      />
      <div className="flex justify-between text-muted-foreground text-xs tabular-nums">
        <span>{min}</span>
        <span>{max}</span>
      </div>
      {error && <FieldError errors={[error]} />}
    </Field>
  );
}

/** The plain-English window the two limits make, for a quick sanity check. */
export function bookingWindowSummary(
  minDays: number | undefined,
  maxDays: number | undefined,
  today: Date = new Date()
): string | null {
  if (
    typeof minDays !== 'number' ||
    typeof maxDays !== 'number' ||
    !Number.isFinite(minDays) ||
    !Number.isFinite(maxDays) ||
    maxDays < minDays
  ) {
    return null;
  }
  const day = (offset: number) => format(addDays(today, offset), 'EEE d MMM');
  const from =
    minDays === 0
      ? `today (${day(0)})`
      : minDays === 1
        ? `tomorrow (${day(1)})`
        : day(minDays);
  return `Right now, customers can book online from ${from} to ${day(maxDays)}.`;
}
